import { browserLogger } from '@newfront-insurance/dd-rum';
import JSZip from 'jszip';

const LOCAL_FOLDER_PICKER_ID = 'nova-local-folder-attachment';
const LOCAL_FOLDER_BUNDLE_MIME_TYPE = 'application/zip';
const LOCAL_FOLDER_BUNDLE_FILE_LIMIT = 5000;

type FileSystemPermissionMode = 'read' | 'readwrite';

export type FileSystemHandleLike = {
  kind: 'file' | 'directory';
  name: string;
};

export type FileSystemFileHandleLike = FileSystemHandleLike & {
  kind: 'file';
  getFile: () => Promise<File>;
};

export type FileSystemDirectoryHandleLike = FileSystemHandleLike & {
  kind: 'directory';
  entries: () => AsyncIterableIterator<[string, FileSystemHandleLike]>;
};

type WindowWithDirectoryPicker = Window & {
  showDirectoryPicker?: (options?: {
    id?: string;
    mode?: FileSystemPermissionMode;
    startIn?: 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos' | FileSystemHandleLike;
  }) => Promise<FileSystemDirectoryHandleLike>;
};

export interface LocalFolderBundleFileEntry {
  path: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
}

export interface LocalFolderBundleSkippedEntry {
  path: string;
  reason: 'read_error' | 'file_limit';
  message?: string;
}

export interface LocalFolderBundleMetadata {
  kind: 'folder';
  folderName: string;
  fileCount: number;
  totalBytes: number;
  skippedFileCount: number;
  archiveFilename: string;
  truncated: boolean;
}

export interface LocalFolderBundleResult {
  archiveFile: File;
  metadata: LocalFolderBundleMetadata;
}

interface FolderWalkState {
  files: LocalFolderBundleFileEntry[];
  skippedEntries: LocalFolderBundleSkippedEntry[];
  totalBytes: number;
  truncated: boolean;
}

export class LocalFolderPickerUnsupportedError extends Error {
  constructor() {
    super('Local folder attachments are not supported in this browser.');
    this.name = 'LocalFolderPickerUnsupportedError';
  }
}

function serializeErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Unknown error';
}

function sanitizePathSegment(value: string) {
  return value.replace(/[\\/]/g, '-') || 'folder';
}

export function buildLocalFolderArchiveFilename(folderName: string) {
  const safeFolderName = sanitizePathSegment(folderName)
    .replace(/\.zip$/i, '')
    .replace(/\s+/g, ' ')
    .trim();

  return `${safeFolderName || 'folder'}-nova-folder.zip`;
}

async function addDirectoryToZip({
  directoryHandle,
  zipRoot,
  relativePath,
  state,
}: {
  directoryHandle: FileSystemDirectoryHandleLike;
  zipRoot: JSZip;
  relativePath: string[];
  state: FolderWalkState;
}) {
  for await (const [entryName, childHandle] of directoryHandle.entries()) {
    const childPath = [...relativePath, sanitizePathSegment(entryName)];
    const displayPath = childPath.join('/');

    if (childHandle.kind === 'directory') {
      zipRoot.folder(childPath.join('/'));
      await addDirectoryToZip({
        directoryHandle: childHandle as FileSystemDirectoryHandleLike,
        zipRoot,
        relativePath: childPath,
        state,
      });
      continue;
    }

    if (state.files.length >= LOCAL_FOLDER_BUNDLE_FILE_LIMIT) {
      state.truncated = true;
      state.skippedEntries.push({
        path: displayPath,
        reason: 'file_limit',
      });
      continue;
    }

    try {
      const file = await (childHandle as FileSystemFileHandleLike).getFile();
      const zipPath = childPath.join('/');

      zipRoot.file(zipPath, file);
      state.files.push({
        path: displayPath,
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
      });
      state.totalBytes += file.size;
    } catch (error) {
      state.skippedEntries.push({
        path: displayPath,
        reason: 'read_error',
        message: serializeErrorMessage(error),
      });
    }
  }
}

export function isLocalFolderPickerSupported() {
  return (
    typeof window !== 'undefined' && typeof (window as WindowWithDirectoryPicker).showDirectoryPicker === 'function'
  );
}

export async function buildLocalFolderBundle(
  directoryHandle: FileSystemDirectoryHandleLike,
): Promise<LocalFolderBundleResult> {
  const zip = new JSZip();
  const archiveFilename = buildLocalFolderArchiveFilename(directoryHandle.name);
  const state: FolderWalkState = {
    files: [],
    skippedEntries: [],
    totalBytes: 0,
    truncated: false,
  };

  await addDirectoryToZip({
    directoryHandle,
    zipRoot: zip,
    relativePath: [],
    state,
  });

  const archiveBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
  });

  return {
    archiveFile: new File([archiveBlob], archiveFilename, {
      type: LOCAL_FOLDER_BUNDLE_MIME_TYPE,
    }),
    metadata: {
      kind: 'folder',
      folderName: directoryHandle.name,
      fileCount: state.files.length,
      totalBytes: state.totalBytes,
      skippedFileCount: state.skippedEntries.length,
      archiveFilename,
      truncated: state.truncated,
    },
  };
}

export async function pickLocalFolderDirectory(): Promise<FileSystemDirectoryHandleLike | null> {
  const showDirectoryPicker = (window as WindowWithDirectoryPicker).showDirectoryPicker;

  if (typeof showDirectoryPicker !== 'function') {
    throw new LocalFolderPickerUnsupportedError();
  }

  let directoryHandle: FileSystemDirectoryHandleLike;

  try {
    directoryHandle = await showDirectoryPicker.call(window, {
      id: LOCAL_FOLDER_PICKER_ID,
      mode: 'read',
      startIn: 'documents',
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      browserLogger.info('[Nova] Local folder picker was cancelled');
      return null;
    }

    throw error;
  }

  return directoryHandle;
}

export async function pickAndBuildLocalFolderBundle(): Promise<LocalFolderBundleResult | null> {
  const directoryHandle = await pickLocalFolderDirectory();

  if (!directoryHandle) {
    return null;
  }

  return buildLocalFolderBundle(directoryHandle);
}
